import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Send,
  Languages,
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  ShieldCheck,
  Gift,
  Sparkles,
  ExternalLink,
  MessageSquare,
  BookOpen
} from 'lucide-react';
import { CONTACT_INFO } from '../data/services';
import { BookingRecord } from '../utils/excelExport';
import { createBookingOrder } from '../services/bookingsService';
import { getVisibleSlotsForDate, getLocalTomorrowDateString } from '../services/slotService';

export type ChatLanguage = 'en' | 'hi';

export interface ChatActionOption {
  label: string;
  action: string;
  icon?: string;
  payload?: any;
}

export interface ChatMessage {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  options?: ChatActionOption[];
  viewType?: 'normal' | 'services' | 'bundles' | 'slots' | 'areas' | 'safety' | 'confirmed';
  bookingSummary?: {
    serviceName: string;
    slot: string;
    ref: string;
    clientName?: string;
    phone?: string;
    address?: string;
    startOtp?: string;
  };
  timestamp: string;
}

interface BlishkaChatbotProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenBookingModal: () => void;
  onNavigateToMenu: () => void;
  isPageView?: boolean;
}

const POPULAR_SERVICES = [
  { id: 'th-combo', name: 'Eyebrow + Upper Lip Threading', price: 80, time: '15 mins', category: 'Threading' },
  { id: 'wax-combo', name: 'Full Arms + Full Legs Waxing', price: 548, time: '35 mins', category: 'Waxing' },
  { id: 'cl-radiance', name: 'Instant Radiance Cleanup', price: 399, time: '30 mins', category: 'Cleanup' },
  { id: 'fac-bridal', name: 'O3+ Diamond Glow Facial', price: 999, time: '55 mins', category: 'Facial' },
  { id: 'spa-nourish', name: 'Nourishing Hair Spa', price: 599, time: '40 mins', category: 'Hair Care' }
];

const VALUE_BUNDLES = [
  { id: 'bundle-everyday', name: 'Blishka Everyday Combo', price: 499, original: 580, savings: 81, desc: 'Eyebrow + Upper Lip + Radiance Cleanup' },
  { id: 'bundle-glow', name: 'Blishka Glow Combo', price: 599, original: 730, savings: 131, desc: 'Cleanup + Threading + Gentle Face Massage' },
  { id: 'bundle-wax', name: 'Wax & Smooth Combo', price: 699, original: 850, savings: 151, desc: 'Full Arms + Full Legs + Honey Underarms' },
  { id: 'bundle-selfcare', name: 'Blishka Self-Care Treat', price: 899, original: 1098, savings: 199, desc: 'Gold Radiance Facial + Classic Manicure' },
  { id: 'bundle-complete', name: 'Blishka Head-to-Toe Care', price: 1199, original: 1450, savings: 251, desc: 'Complete Parlour Grooming for Festive/Occasion' }
];

// Verification and Validation Helpers for Chatbot Booking
const extractPhone = (text: string): { phone: string; matchText: string } | null => {
  const regex = /(?:(?:\+91|91|0)[\s-]*)?([6-9]\d{4}[\s-]?\d{5})\b/;
  const match = text.match(regex);
  if (match) {
    const rawMatch = match[0];
    const cleanDigits = rawMatch.replace(/\D/g, '');
    const tenDigits = cleanDigits.slice(-10);
    if (/^[6-9]\d{9}$/.test(tenDigits)) {
      return { phone: tenDigits, matchText: rawMatch };
    }
  }
  return null;
};

const hasInvalidPhoneAttempt = (text: string): boolean => {
  const digitsOnly = text.replace(/\D/g, '');
  if (digitsOnly.length >= 6 && digitsOnly.length <= 13) {
    const lastTen = digitsOnly.slice(-10);
    if (!/^[6-9]\d{9}$/.test(lastTen)) {
      return true;
    }
  }
  return false;
};

const INVALID_NAMES = new Set([
  'ok', 'yes', 'no', 'hi', 'hello', 'hey', 'test', 'booking', 'address', 'book',
  'plz', 'please', 'done', 'naam', 'name', 'phone', 'number', 'haan', 'ha', 'nahi',
  'bhilai', 'durg', 'home', 'services', 'service', 'parlour', 'salon', 'blishka',
  'good', 'thanks', 'thank you', 'karo', 'kardo', 'chahiye', 'chahiye tha', 'slot',
  'pm', 'am', 'today', 'tomorrow', 'urgent'
]);

const cleanCandidateName = (candidate: string): string => {
  return candidate
    .replace(/[^a-zA-Z\u0900-\u097F\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isNameValid = (candidate: string): boolean => {
  const clean = cleanCandidateName(candidate);
  if (clean.length < 2) return false;
  if (/^\d+$/.test(clean)) return false;
  if (INVALID_NAMES.has(clean.toLowerCase())) return false;
  return true;
};

const INVALID_ADDRESSES = new Set([
  'bhilai', 'durg', 'home', 'my home', 'ghar', 'ghar par', 'address', 'my address',
  'pata', 'ok', 'yes', 'done', 'test', 'booking', 'doorstep', 'near me', 'please come',
  'send someone', 'send beautician', 'sector', 'supela', 'durg bhilai', 'chhattisgarh'
]);

const isAddressValid = (address: string): boolean => {
  const clean = address.trim().toLowerCase();
  if (clean.length < 8) return false;
  if (INVALID_ADDRESSES.has(clean)) return false;
  const words = clean.split(/\s+/).filter(w => w.length > 1);
  if (words.length < 2) return false;
  return true;
};

export const BlishkaChatbot: React.FC<BlishkaChatbotProps> = ({
  isOpen,
  onClose,
  onOpenBookingModal,
  onNavigateToMenu,
  isPageView = false
}) => {
  const [lang, setLang] = useState<ChatLanguage>('en');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // In-chat booking state
  const [tempService, setTempService] = useState<{ id: string; name: string; price: number } | null>(null);
  const [bookingState, setBookingState] = useState<{
    step: 'idle' | 'awaiting_slot' | 'awaiting_contact' | 'awaiting_name' | 'awaiting_address';
    service: { id: string; name: string; price: number } | null;
    slot: string;
    clientName: string;
    phone: string;
    address: string;
  }>({
    step: 'idle',
    service: null,
    slot: '10:00 AM - 12:00 PM',
    clientName: '',
    phone: '',
    address: ''
  });

  const [lastConfirmedBooking, setLastConfirmedBooking] = useState<{
    refId: string;
    serviceName: string;
    slot: string;
    name: string;
    phone: string;
    address: string;
  } | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Initialize first welcome message
  useEffect(() => {
    if (messages.length === 0) {
      initWelcomeMessage(lang);
    }
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, isOpen]);

  const getMainShortcuts = (currentLang: ChatLanguage): ChatActionOption[] => {
    const isHi = currentLang === 'hi';
    return [
      { label: isHi ? '🌸 सेवा बुक करें' : '🌸 Book Now', action: 'start_booking' },
      { label: isHi ? '💰 रेट लिस्ट' : '💰 Rates', action: 'services_info' },
      { label: isHi ? '🎁 स्पेशल बंडल्स' : '🎁 Bundles', action: 'bundles_info' },
      { label: isHi ? '📖 पूरा मेनू देखें' : '📖 View Full Menu', action: 'navigate_to_menu' },
      { label: isHi ? '📍 सेवा क्षेत्र' : '📍 Areas', action: 'areas_info' },
      { label: isHi ? '🛡️ स्वच्छता मानक' : '🛡️ Hygiene', action: 'safety_info' },
      { label: isHi ? '💬 व्हाट्सएप' : '💬 WhatsApp', action: 'whatsapp_support' }
    ];
  };

  const initWelcomeMessage = (currentLang: ChatLanguage) => {
    const isHi = currentLang === 'hi';
    const welcome: ChatMessage = {
      id: 'welcome-msg',
      sender: 'bot',
      text: isHi
        ? 'नमस्ते! मैं **Blishka स्मार्ट सहायिका** हूँ। भिलाई (सेक्टर 1-10 एवं प्रगति नगर) में आपके घर पर पार्लर सेवा के लिए मैं यहाँ हूँ।\n\nआप नीचे दिए गए विकल्पों में से किसी पर भी टैप कर सकते हैं या अपना प्रश्न लिख सकते हैं:'
        : 'Hello! I am your **Blishka Assistant**. I can help you book doorstep parlour services across Bhilai (Sector 1 to 10 & Pragati Nagar).\n\nChoose an option below or type your question:',
      options: getMainShortcuts(currentLang),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages([welcome]);
  };

  const handleToggleLanguage = () => {
    const nextLang: ChatLanguage = lang === 'en' ? 'hi' : 'en';
    setLang(nextLang);

    const switchNotice: ChatMessage = {
      id: Date.now().toString(),
      sender: 'bot',
      text: nextLang === 'hi'
        ? '🌐 भाषा बदलकर **हिंदी** कर दी गई है। मैं आपकी कैसे सहायता कर सकती हूँ?'
        : '🌐 Language switched to **English**. How can I help you today?',
      options: getMainShortcuts(nextLang),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages((prev) => [...prev, switchNotice]);
  };

  const addBotResponse = (
    text: string,
    options?: ChatActionOption[],
    viewType: ChatMessage['viewType'] = 'normal',
    bookingSummary?: ChatMessage['bookingSummary']
  ) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          sender: 'bot',
          text,
          options,
          viewType,
          bookingSummary,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    }, 400);
  };

  const completeBooking = ({
    name,
    phone,
    address,
    service,
    slot
  }: {
    name: string;
    phone: string;
    address: string;
    service: { id: string; name: string; price: number } | null;
    slot: string;
  }) => {
    const isHi = lang === 'hi';
    const serviceName = service?.name || tempService?.name || 'Blishka Salon Care';
    const servicePrice = service?.price || tempService?.price || 499;
    const refId = `#BL-${Math.floor(1000 + Math.random() * 9000)}`;
    const startOtp = Math.floor(1000 + Math.random() * 9000).toString();
    const confirmedSlot = slot || bookingState.slot || '10:00 AM - 12:00 PM';

    const newBookingRecord: BookingRecord = {
      bookingRef: refId,
      timestamp: new Date().toISOString(),
      clientName: name,
      phone: phone,
      area: 'Bhilai / Durg',
      address: address,
      preferredDate: getLocalTomorrowDateString(),
      preferredSlot: confirmedSlot,
      specialRequests: 'Chatbot Verified Doorstep Booking',
      startOtp: startOtp,
      paymentMethod: 'Pay After Service',
      paymentStatus: 'Pending Collection',
      serviceStatus: 'assigned',
      servicePerson: 'Pooja Verma (Senior Beautician)',
      partnerId: 'staff-1',
      partnerName: 'Pooja Verma',
      partnerPhone: '9876500001',
      items: [
        {
          id: service?.id || tempService?.id || 'chat-svc',
          name: serviceName,
          price: servicePrice,
          category: 'Salon Care'
        }
      ],
      totalPrice: servicePrice,
      totalOriginalPrice: Math.round(servicePrice * 1.25),
      totalSavings: Math.round(servicePrice * 0.25),
      bookingSource: 'web'
    };

    try {
      createBookingOrder(newBookingRecord);
    } catch (err) {
      console.warn('Silent booking save log:', err);
    }

    setLastConfirmedBooking({
      refId,
      serviceName,
      slot: confirmedSlot,
      name,
      phone,
      address
    });

    addBotResponse(
      isHi
        ? `🎉 **बुकिंग सफलतापूर्वक दर्ज हो गई है!**\n\n• **बुकिंग संदर्भ:** ${refId}\n• **सेवा:** ${serviceName} (₹${servicePrice})\n• **समय:** ${confirmedSlot}\n• **ग्राहक:** ${name}\n• **मोबाइल:** ${phone}\n• **पता:** ${address}\n• **सर्विस OTP:** ${startOtp}\n\nहमारी वेरीफाइड महिला ब्यूटीशियन समय पर आपके घर पहुँचेंगी। सेवा पूरी होने के बाद Cash या UPI से भुगतान करें।`
        : `🎉 **Booking Successfully Received!**\n\n• **Booking Ref:** ${refId}\n• **Service:** ${serviceName} (₹${servicePrice})\n• **Slot:** ${confirmedSlot}\n• **Client:** ${name}\n• **Mobile:** ${phone}\n• **Address:** ${address}\n• **Service OTP:** ${startOtp}\n\nOur verified female professional will arrive at your doorstep. Pay conveniently after your service via Cash or UPI.`,
      [
        { label: isHi ? '💬 व्हाट्सएप पर कन्फर्म करें' : '💬 Confirm on WhatsApp', action: 'whatsapp_support' },
        { label: isHi ? '📖 और सेवाएं देखें' : '📖 Explore More Services', action: 'navigate_to_menu' }
      ],
      'confirmed',
      {
        serviceName,
        slot: confirmedSlot,
        ref: refId,
        clientName: name,
        phone: phone,
        address: address,
        startOtp: startOtp
      }
    );

    setBookingState({
      step: 'idle',
      service: null,
      slot: '10:00 AM - 12:00 PM',
      clientName: '',
      phone: '',
      address: ''
    });
    setTempService(null);
  };

  const handleOptionClick = (action: string, payload?: any) => {
    const isHi = lang === 'hi';

    if (action === 'start_booking') {
      setBookingState({
        step: 'idle',
        service: null,
        slot: '10:00 AM - 12:00 PM',
        clientName: '',
        phone: '',
        address: ''
      });
      setTempService(null);
      addBotResponse(
        isHi
          ? 'कृपया अपनी पसंदीदा सेवा या बंडल चुनें जिसे आप घर पर बुक करना चाहते हैं:'
          : 'Please select the service or bundle you would like to book at your doorstep:',
        undefined,
        'services'
      );
    } else if (action === 'select_service_item') {
      setTempService(payload);
      setBookingState(prev => ({
        ...prev,
        step: 'awaiting_slot',
        service: payload
      }));
      addBotResponse(
        isHi
          ? `आपने चुना: **${payload.name}** (₹${payload.price})।\nकृपया अपना पसंदीदा **समय स्लॉट (10:00 AM – 6:00 PM)** चुनें:`
          : `Selected: **${payload.name}** (₹${payload.price}).\nPlease select your preferred **doorstep slot (10:00 AM – 6:00 PM)**:`,
        undefined,
        'slots'
      );
    } else if (action === 'select_slot_item') {
      const selectedSlot = payload.slot || '10:00 AM - 12:00 PM';
      const svcName = bookingState.service?.name || tempService?.name || 'Home Salon Care';
      setBookingState(prev => ({
        ...prev,
        step: 'awaiting_contact',
        slot: selectedSlot
      }));
      addBotResponse(
        isHi
          ? `समय स्लॉट: **${selectedSlot}** (${svcName}) चुन लिया गया है!\n\nहोम सर्विस बुकिंग के लिए कृपया अपना **पूरा नाम और 10-अंकों का मोबाइल नंबर** दर्ज करें (उदा: *Priya Sharma 9876543210*):`
          : `Doorstep Slot: **${selectedSlot}** selected for **${svcName}**!\n\nTo confirm your appointment, please reply with your **Full Name & 10-digit Phone Number** (e.g. *Priya Sharma 9876543210*):`
      );
    } else if (action === 'services_info') {
      addBotResponse(
        isHi
          ? 'यहाँ ब्लिश्का की लोकप्रिय सेवाएं और पारदर्शी दरें दी गई हैं:'
          : 'Here are Blishka’s popular doorstep salon services and transparent rates:',
        [
          { label: isHi ? '🌸 सेवा बुक करें' : '🌸 Book Service', action: 'start_booking' },
          { label: isHi ? '📖 पूरा मेनू पेज पर देखें' : '📖 View Full Menu on Page', action: 'navigate_to_menu' },
          { label: isHi ? '🎁 बंडल्स देखें' : '🎁 View Bundles', action: 'bundles_info' }
        ],
        'services'
      );
    } else if (action === 'bundles_info') {
      addBotResponse(
        isHi
          ? '🌟 **ब्लिश्का मनी-सेविंग बंडल्स (घर पर पार्लर):**\nअधिकतम बचत के लिए हमारे लोकप्रिय पैकेज:'
          : '🌟 **Blishka Value Bundles (Doorstep Care):**\nOur curated combos crafted for high savings and zero hassle:',
        [
          { label: isHi ? '🌸 बंडल बुक करें' : '🌸 Book a Bundle', action: 'start_booking' },
          { label: isHi ? '📖 वेबसाइट पर मेनू देखें' : '📖 Explore Menu on Page', action: 'navigate_to_menu' }
        ],
        'bundles'
      );
    } else if (action === 'navigate_to_menu') {
      // Closes the chatbot and scrolls cleanly down to the Services Catalog section
      onNavigateToMenu();
    } else if (action === 'areas_info') {
      addBotResponse(
        isHi
          ? '📍 **सेवा क्षेत्र एवं कार्य समय (Bhilai):**\n\n• **सक्रिय क्षेत्र:** भिलाई सेक्टर 1 से 10 एवं प्रगति नगर\n• **अन्य क्षेत्र:** जल्द ही शुरू हो रहे हैं!\n• **समय:** सुबह 9:00 AM से शाम 7:30 PM (सातों दिन)'
          : '📍 **Service Locations & Timings (Bhilai):**\n\n• **Active Service Areas:** Bhilai Sector 1 to 10 & Pragati Nagar\n• **Other Areas:** Expanding soon!\n• **Operating Hours:** 9:00 AM – 7:30 PM (All 7 Days)',
        [
          { label: isHi ? '🌸 अभी बुक करें' : '🌸 Book Appointment', action: 'start_booking' },
          { label: isHi ? '💬 व्हाट्सएप पर पूछें' : '💬 Inquire on WhatsApp', action: 'whatsapp_support' }
        ],
        'areas'
      );
    } else if (action === 'safety_info') {
      addBotResponse(
        isHi
          ? '🛡️ **100% स्वच्छता और सुरक्षा मानक:**\n\n• **सिंगल-यूज़ डिस्पोजेबल:** हर क्लाइंट के लिए नई बेडशीट, गाउन और वाइप्स\n• **सैनिटाइज्ड टूल्स:** प्रत्येक सेवा के बाद टूल्स का स्टेरिलाइजेशन\n• **वेरीफाइड महिला विशेषज्ञ:** बैकग्राउंड-वेरीफाइड एवं ट्रेंड प्रोफेशनल्स\n• **ओरिजिनल ब्रांड्स:** L\'Oreal, Cheryl\'s, O3+, Lotus आदि'
          : '🛡️ **100% Hygiene & Safety Protocols:**\n\n• **Single-Use Disposables:** Fresh bedsheets, gowns, wooden spatulas & wipes for every client\n• **Tool Sterilization:** Strict antiseptic cleaning before and after every appointment\n• **Verified Female Experts:** Background-checked, professionally certified beauticians\n• **Original Sealed Products:** Cheryl’s, O3+, L’Oreal, Lotus and branded cosmetics only',
        [
          { label: isHi ? '🌸 सेवा बुक करें' : '🌸 Book Service', action: 'start_booking' },
          { label: isHi ? '📖 मेनू देखें' : '📖 View Menu', action: 'navigate_to_menu' }
        ],
        'safety'
      );
    } else if (action === 'whatsapp_support') {
      const waText = lastConfirmedBooking
        ? (isHi
            ? `नमस्ते ब्लिश्का! मैंने चैटबॉट से बुकिंग दर्ज की है:\n• संदर्भ: ${lastConfirmedBooking.refId}\n• सेवा: ${lastConfirmedBooking.serviceName}\n• समय: ${lastConfirmedBooking.slot}\n• नाम: ${lastConfirmedBooking.name}\n• फ़ोन: ${lastConfirmedBooking.phone}\n• पता: ${lastConfirmedBooking.address}`
            : `Hi Blishka! I booked an appointment via your Assistant:\n• Ref: ${lastConfirmedBooking.refId}\n• Service: ${lastConfirmedBooking.serviceName}\n• Slot: ${lastConfirmedBooking.slot}\n• Name: ${lastConfirmedBooking.name}\n• Phone: ${lastConfirmedBooking.phone}\n• Address: ${lastConfirmedBooking.address}`)
        : (isHi
            ? 'नमस्ते ब्लिश्का! मुझे होम पार्लर सेवा के बारे में पूछना है।'
            : 'Hi Blishka! I would like to inquire about home parlour services.');

      window.open(
        `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(waText)}`,
        '_blank'
      );
    } else if (action === 'view_cart') {
      onOpenBookingModal();
    }
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    const text = inputText.trim();
    if (!text) return;

    // Append user message
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputText('');

    const lower = text.toLowerCase();
    const isHi = lang === 'hi';

    // 0. Handle cancellation / reset
    if (lower === 'cancel' || lower === 'रद्द' || lower === 'stop' || lower === 'reset') {
      setBookingState({
        step: 'idle',
        service: null,
        slot: '10:00 AM - 12:00 PM',
        clientName: '',
        phone: '',
        address: ''
      });
      setTempService(null);
      addBotResponse(
        isHi
          ? 'बुकिंग प्रक्रिया रीसेट कर दी गई है। आप जब चाहें नई सेवा चुन सकते हैं:'
          : 'Booking process has been reset. You can select a service or ask any question:',
        getMainShortcuts('hi')
      );
      return;
    }

    // 1. STEP A: Currently awaiting address
    if (bookingState.step === 'awaiting_address') {
      if (isAddressValid(text)) {
        completeBooking({
          name: bookingState.clientName,
          phone: bookingState.phone,
          address: text.trim(),
          service: bookingState.service || tempService,
          slot: bookingState.slot || '11:00 AM'
        });
        return;
      } else {
        addBotResponse(
          isHi
            ? '⚠️ **कृपया पूरा और सही पता दर्ज करें:**\nडोरस्टेप सेवा के लिए मकान/फ्लैट नं., सेक्टर/प्रगति नगर एवं निकटतम लैंडमार्क अनिवार्य है (उदा: *Street 14, Sector 6, Bhilai*):'
            : '⚠️ **Please enter a complete doorstep address:**\nHouse/Flat No., Sector/Pragati Nagar, and a nearby landmark are required so our beautician can reach seamlessly (e.g., *Street 14, Sector 6, Bhilai*):'
        );
        return;
      }
    }

    // 2. STEP B: Currently awaiting customer name (Phone already captured)
    if (bookingState.step === 'awaiting_name') {
      const candidate = cleanCandidateName(text);
      if (isNameValid(candidate)) {
        setBookingState(prev => ({
          ...prev,
          step: 'awaiting_address',
          clientName: candidate
        }));
        addBotResponse(
          isHi
            ? `✅ **नाम:** ${candidate}\n📱 **फ़ोन:** ${bookingState.phone}\n\n📍 अब कृपया सेवा के लिए अपना **विस्तृत पता (Doorstep Address)** दर्ज करें:\n(उदा: *मकान/फ्लैट नं., सेक्टर 1-10 या प्रगति नगर, लैंडमार्क, भिलाई*)`
            : `✅ **Name:** ${candidate}\n📱 **Phone:** ${bookingState.phone}\n\n📍 Now please provide your **detailed doorstep address** for the beautician's visit:\n(e.g., *House/Flat No., Sector 1-10 or Pragati Nagar, Landmark, Bhilai*)`
        );
        return;
      } else {
        addBotResponse(
          isHi
            ? '⚠️ **कृपया मान्य नाम दर्ज करें** (कम से कम 2 अक्षर, केवल नाम, उदा: *Priya Sharma*):'
            : '⚠️ **Please enter a valid customer name** (at least 2 letters, e.g. *Priya Sharma*):'
        );
        return;
      }
    }

    // 3. STEP C: Check for valid Phone number in input
    const phoneResult = extractPhone(text);
    if (phoneResult) {
      const detectedPhone = phoneResult.phone;
      const remaining = text.replace(phoneResult.matchText, '').trim();

      // Check if user entered Name + Phone + Full Address in one single message!
      const chunks = remaining.split(/[,;\n]+/).map(c => c.trim()).filter(Boolean);
      if (chunks.length >= 2) {
        const candidateName = cleanCandidateName(chunks[0]);
        const candidateAddress = chunks.slice(1).join(', ').trim();
        if (isNameValid(candidateName) && isAddressValid(candidateAddress)) {
          completeBooking({
            name: candidateName,
            phone: detectedPhone,
            address: candidateAddress,
            service: bookingState.service || tempService,
            slot: bookingState.slot || '11:00 AM'
          });
          return;
        }
      }

      // Check if remaining contains a valid name
      const candidateName = cleanCandidateName(remaining);
      if (isNameValid(candidateName)) {
        setBookingState(prev => ({
          ...prev,
          step: 'awaiting_address',
          clientName: candidateName,
          phone: detectedPhone
        }));
        addBotResponse(
          isHi
            ? `✅ **नाम:** ${candidateName}\n📱 **फ़ोन:** ${detectedPhone}\n\n📍 कृपया सेवा के लिए अपना **विस्तृत पता (Doorstep Address)** दर्ज करें:\n(उदा: *मकान/फ्लैट नं., सेक्टर 1-10 या प्रगति नगर, लैंडमार्क, भिलाई*)`
            : `✅ **Name:** ${candidateName}\n📱 **Phone:** ${detectedPhone}\n\n📍 Please provide your **proper doorstep address** for the beautician's visit:\n(e.g., *House/Flat No., Sector 1-10 or Pragati Nagar, Landmark, Bhilai*)`
        );
        return;
      } else {
        setBookingState(prev => ({
          ...prev,
          step: 'awaiting_name',
          phone: detectedPhone
        }));
        addBotResponse(
          isHi
            ? `📱 **फ़ोन:** ${detectedPhone} (सत्यापित)\n\n👤 कृपया अपना **पूरा नाम** दर्ज करें (उदा: *Priya Sharma*):`
            : `📱 **Phone:** ${detectedPhone} (Verified)\n\n👤 Please enter your **Full Name** (e.g. *Priya Sharma*):`
        );
        return;
      }
    }

    // 4. STEP D: If currently awaiting contact, but no valid phone was found
    if (bookingState.step === 'awaiting_contact') {
      if (hasInvalidPhoneAttempt(text)) {
        addBotResponse(
          isHi
            ? '⚠️ **अमान्य मोबाइल नंबर!** कृपया 10 अंकों का मान्य भारतीय मोबाइल नंबर दर्ज करें (शुरुआत 6, 7, 8 या 9 से, उदा: *9876543210*):'
            : '⚠️ **Invalid mobile number!** Please enter a valid 10-digit Indian mobile number (starting with 6, 7, 8, or 9, e.g. *9876543210*):'
        );
        return;
      }

      const candidateName = cleanCandidateName(text);
      if (isNameValid(candidateName) && !/\d/.test(text)) {
        setBookingState(prev => ({
          ...prev,
          clientName: candidateName
        }));
        addBotResponse(
          isHi
            ? `✅ **नाम:** ${candidateName}\n\n📱 कृपया अपना **10-अंकों का मोबाइल नंबर** दर्ज करें (उदा: *9876543210*):`
            : `✅ **Name:** ${candidateName}\n\n📱 Please enter your **10-digit Mobile Number** (e.g. *9876543210*):`
        );
        return;
      }

      addBotResponse(
        isHi
          ? 'कृपया बुकिंग आगे बढ़ाने के लिए अपना **नाम और 10-अंकों का मोबाइल नंबर** दर्ज करें (उदा: *Priya Sharma 9876543210*):'
          : 'Please reply with your **Name & 10-digit Phone Number** to confirm your appointment (e.g. *Priya Sharma 9876543210*):'
      );
      return;
    }

    // Natural Language Queries
    if (lower.includes('menu') || lower.includes('रेट') || lower.includes('price') || lower.includes('सूची')) {
      handleOptionClick('services_info');
    } else if (lower.includes('book') || lower.includes('बुक') || lower.includes('appointment')) {
      handleOptionClick('start_booking');
    } else if (lower.includes('bundle') || lower.includes('बंडल') || lower.includes('package') || lower.includes('combo')) {
      handleOptionClick('bundles_info');
    } else if (lower.includes('area') || lower.includes('जगह') || lower.includes('bhilai') || lower.includes('durg') || lower.includes('टाइम') || lower.includes('timing')) {
      handleOptionClick('areas_info');
    } else if (lower.includes('safety') || lower.includes('hygiene') || lower.includes('सुरक्षा') || lower.includes('clean')) {
      handleOptionClick('safety_info');
    } else if (lower.includes('whatsapp') || lower.includes('बात') || lower.includes('call') || lower.includes('contact')) {
      handleOptionClick('whatsapp_support');
    } else {
      addBotResponse(
        isHi
          ? 'धन्यवाद! आपकी सहायता के लिए कृपया नीचे दिए गए विकल्पों में से चुनें या हमारे व्हाट्सएप पर संपर्क करें:'
          : 'Thank you! To help you best, please select one of the quick options below or chat with us on WhatsApp:',
        getMainShortcuts(lang)
      );
    }
  };

  // Clean Markdown & Text Renderer
  const renderFormattedText = (text: string) => {
    return text.split('\n').map((line, lIdx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return <div key={lIdx} className="chat-spacer" />;
      }

      const isBullet = trimmed.startsWith('•') || trimmed.startsWith('-');
      const content = isBullet ? trimmed.replace(/^[•-]\s*/, '') : trimmed;

      // Parse bold tags **...**
      const parts = content.split(/(\*\*[^*]+\*\*)/g);
      const elements = parts.map((part, pIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={pIdx} className="chat-bold-highlight">
              {part.slice(2, -2)}
            </strong>
          );
        }
        return part;
      });

      if (isBullet) {
        return (
          <div key={lIdx} className="chat-bullet-line">
            <span className="chat-bullet-icon">•</span>
            <span className="chat-bullet-text">{elements}</span>
          </div>
        );
      }

      return (
        <p key={lIdx} className="chat-text-line">
          {elements}
        </p>
      );
    });
  };

  if (!isOpen) return null;

  const chatContainer = (
    <div className={`chatbot-container ${isPageView ? 'chatbot-page-container' : ''}`} onClick={(e) => e.stopPropagation()}>
      {/* Chatbot Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-left">
            <div className="chatbot-avatar-wrap">
              <img src="/brand-logo.png" alt="Blishka Assistant" className="chatbot-brand-avatar" />
              <span className="chatbot-online-indicator" />
            </div>

            <div className="chatbot-meta">
              <div className="chatbot-title-line">
                <h3 className="chatbot-title">
                  {lang === 'hi' ? 'ब्लिश्का सहायिका' : 'Blishka Assistant'}
                </h3>
                <span className="chatbot-verified-badge">Verified</span>
              </div>
              <p className="chatbot-subtitle">
                {lang === 'hi' ? 'ऑनलाइन · भिलाई व दुर्ग होम सर्विस' : 'Online · Doorstep Parlour Care'}
              </p>
            </div>
          </div>

          <div className="chatbot-header-actions">
            {/* Language Switcher */}
            <button
              type="button"
              className="chatbot-lang-switch"
              onClick={handleToggleLanguage}
              title={lang === 'en' ? 'हिंदी में बदलें' : 'Switch to English'}
              aria-label="Toggle language"
            >
              <Languages size={14} />
              <span className="lang-active-tag">{lang === 'en' ? 'हिंदी' : 'EN'}</span>
            </button>

            {/* Close Button */}
            <button
              type="button"
              className="chatbot-close-button"
              onClick={onClose}
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Top Sticky Quick Topics Carousel */}
        <div className="chatbot-shortcuts-bar" aria-label="Quick assistance topics">
          <button
            type="button"
            className="chatbot-shortcut-pill primary"
            onClick={() => handleOptionClick('start_booking')}
          >
            <Calendar size={13} />
            <span>{lang === 'hi' ? 'बुकिंग करें' : 'Book Now'}</span>
          </button>

          <button
            type="button"
            className="chatbot-shortcut-pill"
            onClick={() => handleOptionClick('services_info')}
          >
            <Sparkles size={13} />
            <span>{lang === 'hi' ? 'रेट लिस्ट' : 'Price List'}</span>
          </button>

          <button
            type="button"
            className="chatbot-shortcut-pill"
            onClick={() => handleOptionClick('bundles_info')}
          >
            <Gift size={13} />
            <span>{lang === 'hi' ? 'बंडल्स' : 'Bundles'}</span>
          </button>

          <button
            type="button"
            className="chatbot-shortcut-pill highlight"
            onClick={() => handleOptionClick('navigate_to_menu')}
          >
            <BookOpen size={13} />
            <span>{lang === 'hi' ? 'पूरा मेनू' : 'Full Menu'}</span>
          </button>

          <button
            type="button"
            className="chatbot-shortcut-pill"
            onClick={() => handleOptionClick('areas_info')}
          >
            <MapPin size={13} />
            <span>{lang === 'hi' ? 'एरिया' : 'Areas'}</span>
          </button>

          <button
            type="button"
            className="chatbot-shortcut-pill"
            onClick={() => handleOptionClick('safety_info')}
          >
            <ShieldCheck size={13} />
            <span>{lang === 'hi' ? 'सुरक्षा' : 'Hygiene'}</span>
          </button>

          <button
            type="button"
            className="chatbot-shortcut-pill whatsapp"
            onClick={() => handleOptionClick('whatsapp_support')}
          >
            <MessageSquare size={13} />
            <span>WhatsApp</span>
          </button>
        </div>

        {/* Messages Scroll Area */}
        <div className="chatbot-scroll-area">
          {messages.map((msg) => (
            <div key={msg.id} className={`chat-message-row ${msg.sender}`}>
              <div className={`chat-bubble-container ${msg.viewType ? 'has-cards' : ''}`}>
                <div className={`chat-bubble ${msg.sender}`}>
                  {renderFormattedText(msg.text)}

                  {/* Clean Service Cards Attachment */}
                  {msg.viewType === 'services' && (
                    <div className="chat-cards-grid">
                      {POPULAR_SERVICES.map((svc) => (
                        <div key={svc.id} className="chat-clean-card">
                          <div className="chat-clean-card-left">
                            <span className="chat-card-title">{svc.name}</span>
                            <span className="chat-card-time">{svc.time}</span>
                          </div>
                          <div className="chat-clean-card-right">
                            <span className="chat-card-price">₹{svc.price}</span>
                            <button
                              type="button"
                              className="chat-card-btn"
                              onClick={() => handleOptionClick('select_service_item', svc)}
                            >
                              {lang === 'hi' ? 'चुनें' : 'Select'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Clean Bundles Cards Attachment */}
                  {msg.viewType === 'bundles' && (
                    <div className="chat-cards-grid">
                      {VALUE_BUNDLES.map((bundle) => (
                        <div key={bundle.id} className="chat-clean-bundle-card">
                          <div className="chat-bundle-header">
                            <span className="chat-bundle-name">{bundle.name}</span>
                            <span className="chat-bundle-save">Save ₹{bundle.savings}</span>
                          </div>
                          <p className="chat-bundle-desc">{bundle.desc}</p>
                          <div className="chat-bundle-footer">
                            <div className="chat-bundle-pricing">
                              <span className="chat-bundle-price">₹{bundle.price}</span>
                              <span className="chat-bundle-orig">₹{bundle.original}</span>
                            </div>
                            <button
                              type="button"
                              className="chat-card-btn"
                              onClick={() =>
                                handleOptionClick('select_service_item', {
                                  id: bundle.id,
                                  name: bundle.name,
                                  price: bundle.price
                                })
                              }
                            >
                              {lang === 'hi' ? 'बंडल बुक करें' : 'Book Bundle'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Clean Hourly Slots Grid Attachment */}
                  {msg.viewType === 'slots' && (
                    <div className="chat-slots-selector-card">
                      <div className="chat-slots-label">
                        <Clock size={13} />
                        <span>{lang === 'hi' ? 'समय स्लॉट चुनें (Doorstep Slot)' : 'Choose Doorstep Slot'}</span>
                      </div>
                      <div className="chat-slots-grid">
                        {getVisibleSlotsForDate(getLocalTomorrowDateString()).map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            className="chat-slot-button"
                            onClick={() => handleOptionClick('select_slot_item', { slot })}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Confirmed Booking Summary Card */}
                  {msg.bookingSummary && (
                    <div className="chat-booking-success-card">
                      <div className="chat-success-header">
                        <CheckCircle2 size={16} className="chat-success-check" />
                        <span className="chat-success-label">
                          {lang === 'hi' ? 'बुकिंग दर्ज (Doorstep Confirmed)' : 'Booking Logged (Doorstep Confirmed)'}
                        </span>
                        <span className="chat-ref-code">{msg.bookingSummary.ref}</span>
                      </div>
                      <div className="chat-success-details">
                        <p><strong>{lang === 'hi' ? 'सेवा:' : 'Service:'}</strong> {msg.bookingSummary.serviceName}</p>
                        {msg.bookingSummary.clientName && (
                          <p><strong>{lang === 'hi' ? 'ग्राहक:' : 'Client:'}</strong> {msg.bookingSummary.clientName}</p>
                        )}
                        {msg.bookingSummary.phone && (
                          <p><strong>{lang === 'hi' ? 'फ़ोन:' : 'Phone:'}</strong> {msg.bookingSummary.phone}</p>
                        )}
                        <p><strong>{lang === 'hi' ? 'समय स्लॉट:' : 'Slot:'}</strong> {msg.bookingSummary.slot}</p>
                        {msg.bookingSummary.address && (
                          <p><strong>{lang === 'hi' ? 'पता:' : 'Address:'}</strong> {msg.bookingSummary.address}</p>
                        )}
                        <p><strong>{lang === 'hi' ? 'शहर:' : 'City:'}</strong> Doorstep Service (Bhilai / Durg)</p>
                        {msg.bookingSummary.startOtp && (
                          <p>
                            <strong>{lang === 'hi' ? 'सुरक्षा OTP:' : 'Service Start OTP:'}</strong>{' '}
                            <span style={{ color: '#059669', fontWeight: 700 }}>{msg.bookingSummary.startOtp}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <span className="chat-message-time">{msg.timestamp}</span>
                </div>

                {/* Action Options Chips */}
                {msg.options && msg.options.length > 0 && (
                  <div className="chat-actions-container">
                    {msg.options.map((opt, oIdx) => (
                      <button
                        key={oIdx}
                        type="button"
                        className="chat-action-pill"
                        onClick={() => handleOptionClick(opt.action, opt.payload)}
                      >
                        {opt.action === 'navigate_to_menu' ? (
                          <>
                            <span>{opt.label}</span>
                            <ExternalLink size={12} />
                          </>
                        ) : (
                          <span>{opt.label}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="chat-message-row bot">
              <div className="chat-typing-bubble">
                <span className="chat-dot" />
                <span className="chat-dot" />
                <span className="chat-dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input Bar */}
        <form className="chatbot-input-container" onSubmit={handleSendMessage}>
          <input
            type="text"
            className="chatbot-input"
            placeholder={
              bookingState.step === 'awaiting_address'
                ? (lang === 'hi'
                    ? 'पूरा पता लिखें (मकान नं., सेक्टर/कॉलोनी, लैंडमार्क)...'
                    : 'Enter doorstep address (House No., Sector, Landmark)...')
                : bookingState.step === 'awaiting_name'
                ? (lang === 'hi'
                    ? 'अपना पूरा नाम दर्ज करें (उदा: Priya Sharma)...'
                    : 'Enter your Full Name (e.g. Priya Sharma)...')
                : bookingState.step === 'awaiting_contact'
                ? (lang === 'hi'
                    ? 'नाम और 10-अंकों का नंबर दर्ज करें (उदा: Priya 9876543210)...'
                    : 'Enter Name & 10-digit phone (e.g. Priya 9876543210)...')
                : (lang === 'hi'
                    ? 'संदेश लिखें या 10-अंकों का नंबर दर्ज करें...'
                    : 'Type your message or 10-digit phone...')
            }
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />

          <button
            type="submit"
            className="chatbot-send-button"
            disabled={!inputText.trim()}
            aria-label="Send message"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
  );

  if (isPageView) {
    return (
      <div className="multipage-view chat-page-view">
        <div className="section-container chat-page-content-container">
          {chatContainer}
        </div>
      </div>
    );
  }

  return (
    <div
      className="chatbot-modal-overlay chatbot-page-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Blishka Assistant"
    >
      {chatContainer}
    </div>
  );
};
